import { fetchRoutes, type RawRoute, type RouteExclude, type TravelMode } from "@/api/routing";
import type { LngLat } from "@/utils/polyline";
import type { DetourTolerance } from "@/utils/profile";

interface FetchBaseRoutesArgs {
  /** Loop fix: intermediate waypoints shape the path without creating
   *  legs/stops ("You have arrived" only at the true end). */
  silentVias?: boolean;
  startCoords: LngLat;
  destCoords: LngLat;
  mode: TravelMode;
  primaryWaypoints: LngLat[];
  detourTolerance: DetourTolerance;
  /** Mapbox `exclude` profile — usually `[]` or `["motorway"]`. */
  baseExcludes: readonly RouteExclude[];
  /** Mapbox highway-avoidance budget in seconds (Infinity = always avoid). */
  noHwyOverrideSec: number;
}

/**
 * Driving routing strategy (other modes fall through to plain Mapbox):
 *  • Scenic   → Valhalla "adventure" only — graph-level avoidance of
 *               motorway + trunk + primary so we never get arterials.
 *               This is appropriate for Scenic because the user explicitly
 *               opted into avoiding everything; some "primaries" are bad
 *               (US-9 commercial strips) and the user is fine missing them.
 *  • Balanced → Mapbox only. The old "Valhalla balanced merged with Mapbox"
 *               approach was creating loops on short trips: Valhalla's
 *               graph-level primary-road penalty forced residential
 *               detours through nice primary roads (Bedford Rd) that
 *               we now KNOW are pleasant. See #139 + #142 (2026-06-02
 *               real-drive investigation). Pleasant relies on Mapbox's
 *               `exclude=motorway` + our named-arterial scoring to
 *               avoid the genuinely bad roads (Taconic, Saw Mill, named
 *               arterials), and lets nice primaries through.
 *  • Fast     → Mapbox only.
 *
 * If the Valhalla path errors or returns nothing, falls back to plain
 * Mapbox so the user always sees something.
 */
export async function fetchBaseRoutesWithFallback({
  startCoords, destCoords, mode, primaryWaypoints,
  detourTolerance, baseExcludes, noHwyOverrideSec, silentVias = false,
}: FetchBaseRoutesArgs): Promise<RawRoute[]> {
  const isScenicTol = detourTolerance === "scenic";
  const useValhallaScenic = mode === "driving" && isScenicTol;

  let baseRaw: RawRoute[] = [];

  if (useValhallaScenic) {
    try {
      const { fetchValhallaRoutes } = await import("@/api/valhalla");
      baseRaw = await fetchValhallaRoutes(
        startCoords, destCoords, primaryWaypoints, "adventure",
      );
    } catch (e) {
      console.warn("[valhalla] scenic failed, falling back to Mapbox:", e);
    }
  }

  // (#142) Pleasant tolerance ("balanced") used to call Valhalla balanced
  // here. That call has been removed — Valhalla's graph-level penalty on
  // primary roads forced residential-detour loops on every short driving
  // trip. Pleasant now uses Mapbox alternatives only, with `exclude=motorway`
  // handled via baseExcludes. Real-drive trigger: Sleepy Hollow → Tarrytown
  // kept showing a residential loop because Valhalla returned it and it
  // scored well on residentialPct + community. Mapbox's natural Bedford
  // Rd alternative is the right Pleasant route, and now it'll surface.

  if (baseRaw.length === 0) {
    baseRaw = await fetchRoutes(
      startCoords, destCoords, mode, primaryWaypoints,
      [...baseExcludes], noHwyOverrideSec, silentVias,
    );
  }

  return baseRaw;
}
